import {
  createContext,
  useContext,
  useCallback,
  useState,
  ReactNode
} from 'react';

import { MainClient, MainClientGroup } from '~types/main';
import { safeJsonParse } from '@/src/utils/storage';

type ClientContextData = {
  client: MainClient;
  setClient: (client: MainClient) => void;
  updateClient: (client: Partial<MainClient>) => void;
  clientGroup: MainClientGroup;
  setClientGroup: (clientGroup: MainClientGroup) => void;
}

const ClientContext = createContext<ClientContextData>({} as ClientContextData);

type ProviderProps = {
  children: ReactNode;
}

export function ClientProvider({ children }: ProviderProps) {
  const [currentClient, setCurrentClient] = useState<MainClient>(() => {
    const client = localStorage.getItem('@auge:client');
    return safeJsonParse<MainClient>(client, {} as MainClient);
  });

  const [currentClientGroup, setCurrentClientGroup] = useState<MainClientGroup>(() => {
    const clientGroup =
      localStorage.getItem('@auge:clientGroup') ??
      localStorage.getItem('@auge:ClientGroup');
    return safeJsonParse<MainClientGroup>(clientGroup, {} as MainClientGroup);
  });

  const updateClient = useCallback((client: Partial<MainClient>) => {
    setCurrentClient(prev => {
      const updatedClient = { ...prev, ...client };
      localStorage.setItem('@auge:client', JSON.stringify(updatedClient));
      return updatedClient;
    });
  }, []);

  const handleSetClient = useCallback((updatedClient: MainClient) => {
    localStorage.setItem('@auge:client', JSON.stringify(updatedClient));
    setCurrentClient(updatedClient);
  }, []);

  const handleSetClientGroup = useCallback((updatedClientGroup: MainClientGroup) => {
    localStorage.setItem('@auge:clientGroup', JSON.stringify(updatedClientGroup));
    setCurrentClientGroup(updatedClientGroup);
  }, []);

  return (
    <ClientContext.Provider
      value={{
        client: currentClient,
        setClient: handleSetClient,
        updateClient,
        clientGroup: currentClientGroup,
        setClientGroup: handleSetClientGroup,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}

export function useClient() {
  const context = useContext(ClientContext);

  if (!context) {
    throw new Error("useClient must be used within a ClientProvider.");
  }

  return context;
}
